import { ccwpApi } from "@/services";
import {
  DOWNLOADDOCUMENT,
  UPLOAD_DOCUMENT,
  UPDATE_DOCUMENT,
  DELETE_DOCUMENT,
} from "../constant/apiPath";

interface FileExtentionsConfig {
  maxFileSize: number;
  zipMaxFileSize: number;
  default: {
    [key: string]: string[];
  };
  import: {
    [key: string]: string[];
  };
  zip: {
    [key: string]: string[];
  };
  msg: {
    [key: string]: string[];
  };
  csv: {
    [key: string]: string[];
  };
  txt: {
    [key: string]: string[];
  };
  viewableTypes: string[];
}

export const fileExtensions: FileExtentionsConfig = {
  //The accepted document format are JPG, JPEG, PNG, DOC, DOCX or PDF
  //The accepted document format are XLSX, XLS, CSV, PDF, DOC, DOCX, TXT, JPG, JPEG, PNG, GIF, TIFF, ZIP
  maxFileSize: 262175457,
  zipMaxFileSize: 500 * 1024 * 1024,
  default: {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel": [".xls"],
    "text/csv": [".csv"],
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/gif": [".gif"],
    "image/tiff": [".tiff"],
    "application/zip": [".zip"],
    "application/vnd.ms-outlook": [".msg"], // Added support for .msg files
  },
  import: {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  },
  zip: {
    "application/zip": [".zip"],
  },
  msg: {
    "application/vnd.ms-outlook": [".msg"],
  },
  csv: {
    "text/csv": [".csv"],
  },
  txt: {
    "text/plain": [".txt"],
  },
  viewableTypes: [
    "application/pdf",
    "text/plain",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/tiff",
  ],
};

export const documentsApi = ccwpApi.injectEndpoints({
  endpoints: (builder) => ({
    downloadDocument: builder.query({
      query: ({ caseId, documentId, fileName = "downloaded_file" }) => ({
        url: DOWNLOADDOCUMENT,
        method: "GET",
        responseHandler: async (response) => {
          if (response?.status === 400) {
            return Promise.reject(response);
          }

          let blob = await response.blob();
          let type = blob.type;

          // if type is a octect-stream but file extension is viewable, create a new blob with the correct type
          if (type === "application/octet-stream") {
            const fileExtension = fileName.split(".").pop();
            const newType = Object.keys(fileExtensions.default).find(
              (key: string) =>
                fileExtensions.default[key]?.includes(`.${fileExtension}`)
            );
            type = newType || type;
            blob = new Blob([blob], { type });
          }

          const url = URL.createObjectURL(blob);
          // opens the file in a new tab if it is viewable
          if (fileExtensions.viewableTypes.includes(type)) {
            window.open(url, "_blank");
            // URL.revokeObjectURL(url);
            // setTimeout(() => URL.revokeObjectURL(url), 10000);
          } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            const downloadControls =
              document.getElementsByClassName("motif-modal-content")[0] ||
              document.body;
            downloadControls.appendChild(a);
            a.click();
            downloadControls.removeChild(a);
            URL.revokeObjectURL(url);
          }
        },
        params: { caseId, documentId },
        transformResponse: (response: { results: any[] }) => response,
        cache: "no-cache",
      }),
    }),

    getDocumentTypes: builder.query<any, void>({
      query: () => ({
        url: "Document/ListDocumentTypes",
        headers: {
          accept: "application/json",
        },
        method: "GET",
      }),
    }),

    uploadDocument: builder.mutation({
      query: (formData) => {
        if (formData?.get("file")?.name?.endsWith(".msg")) {
          const correctedFile = new Blob([formData.get("file")], {
            type: "application/vnd.ms-outlook",
          });
          formData.set("file", correctedFile, formData.get("file").name);
        }

        return {
          url: UPLOAD_DOCUMENT,
          method: "POST",
          body: formData,
        };
      },
    }),

    updateDocument: builder.mutation({
      query: (formData) => ({
        url: UPDATE_DOCUMENT,
        method: "PUT",
        body: formData,
      }),
    }),

    deleteDocument: builder.mutation({
      query: ({ CaseId, DocumentId }) => ({
        url: DELETE_DOCUMENT,
        method: "DELETE",
        params: { CaseId, DocumentId },
      }),
    }),
  }),
});

export const {
  useLazyDownloadDocumentQuery,
  useLazyGetDocumentTypesQuery,
  useUploadDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
} = documentsApi;
